import React from 'react';
import styled from 'astroturf/react';

import { Icon } from 'js/design_system/Icon';
import { ButtonLink } from 'js/design_system/link/ButtonLink';
import PremiumLock from 'js/premium/PremiumLock';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

import { dotVisible } from '../acknowledgment';
import { citationsCell } from '../citationsCell';
import { formatCount } from '../formatCount';
import { NotificationDot } from '../../buttonStyles';
import DoubleDigitCountBubble from '../../DoubleDigitCountBubble';
import type { ImpactStats, NavImpactLinks, PremiumUpsells } from '../../types';
import { segmentFor } from './premiumSegment';
import ProfileHexagonIcon from './icons/profile_hexagon.svg';

export const PROFILE_DROPDOWN_WIDTH = 366;

const TRIGGER = 'acct-mnu-btn';

const Panel = styled.div`
  @import 'stylesheets/design_system/breakpoint';

  position: relative;
  box-sizing: border-box;
  width: ${PROFILE_DROPDOWN_WIDTH}px;
  max-width: 100%;
  min-height: 0;
  padding-top: 24px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: var(--background-white, #ffffff);
  border: 1px solid var(--stroke-light, #dddde2);
  border-radius: var(--border-radius-radius-lg, 16px);
  box-shadow: var(--dropshadow, 0 2px 4px 0 rgba(34, 34, 51, 0.25));

  @media (max-width: ($ds-tablet - 1px)) {
    width: 100%;
    height: 100%;
    border: 0;
    border-radius: 0;
    box-shadow: none;
    pointer-events: auto;
  }
`;

const MobileCloseButton = styled.button`
  @import 'stylesheets/design_system/breakpoint';

  position: absolute;
  top: 8px;
  right: 8px;
  display: none;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border: 0;
  background: transparent;
  color: var(--text-primary, #141413);
  cursor: pointer;

  @media (max-width: ($ds-tablet - 1px)) {
    position: fixed;
    display: inline-flex;
  }
`;

const TopBlock = styled.div`
  padding: 0 24px 20px;
`;

const HeaderLink = styled.a`
  display: flex;
  align-items: center;
  column-gap: 12px;
  padding-bottom: 16px;
  color: var(--text-primary, #141413);
  text-decoration: none;

  &:hover,
  &:focus,
  &:active,
  &:visited {
    color: var(--text-primary, #141413);
    text-decoration: none;
  }
`;

const AvatarImg = styled.img`
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
`;

const HeaderText = styled.div`
  min-width: 0;
  overflow-wrap: break-word;
`;

const UserName = styled.div`
  @import 'stylesheets/design_system/font';
  @include body;

  font-size: 20px;
  font-weight: 600;
  line-height: 32px;
`;

const Affiliation = styled.div`
  @import 'stylesheets/design_system/font';
  @include body__small;
`;

const StatRow = styled.div`
  display: flex;
  column-gap: 12px;
  padding-bottom: 16px;
`;

const StatCard = styled.a`
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  align-items: center;
  padding: 20px 24px;
  border-radius: 8px;
  background: var(--background-beige-light, #f9f8f6);
  color: var(--text-primary, #141413);
  text-decoration: none;

  &:hover,
  &:focus,
  &:active,
  &:visited {
    color: var(--text-primary, #141413);
    text-decoration: none;
  }

  &:hover {
    background: var(--background-light-blue, #eef2f9);
  }
`;

const StatContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: 8px;
`;

const StatLabel = styled.span`
  @import 'stylesheets/design_system/font';
  @include mono;

  color: var(--text-primary, #141413);
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.14px;
  text-transform: uppercase;
  white-space: nowrap;
`;

const StatNumber = styled.span`
  @import 'stylesheets/design_system/font';
  @include mono;

  color: var(--text-primary, #141413);
  font-size: 30px;
  font-weight: 300;
  line-height: 1;
  white-space: nowrap;
`;

const StatDot = styled(NotificationDot)`
  top: -7px;
  right: -8px;
  width: 8px;
  height: 8px;
  background: var(--error-dark, #b60000);
`;

const CtaButtonLink = styled(ButtonLink)`
  --buttons-large-buttons-height: 48px;
  --buttons-large-buttons-border-radius: var(--border-radius-radius-lg, 16px);
  --buttons-large-buttons-l-r-padding: 20px;

  font-weight: var(--type-font-weight-600, 600);
`;

const Section = styled.ul`
  margin: 0;
  padding: 16px 0;
  border-top: 1px solid var(--stroke-light, #dddde2);
  list-style: none;
`;

const RowLink = styled.a`
  @import 'stylesheets/design_system/font';
  @include body__medium;

  display: flex;
  align-items: center;
  height: 32px;
  padding: 0 20px;
  column-gap: 8px;
  border-radius: 4px;
  color: var(--text-primary, #141413);
  text-decoration: none;

  &:hover {
    background: var(--background-light-blue, #eef2f9);
  }

  &:hover,
  &:focus,
  &:active,
  &:visited {
    color: var(--text-primary, #141413);
    text-decoration: none;
  }
`;

const RowIcon = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
`;

const CountPill = styled(DoubleDigitCountBubble)`
  box-sizing: border-box;
  width: auto;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border: none;
  border-radius: var(--border-radius-radius-round, 104px);
  background: var(--error-dark, #b60000);
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
`;

type MenuEntry = {
  label: string;
  icon: React.ReactNode;
  href: string;
  count?: number | null;
  countNoun?: string;
};

const unreadAriaLabel = (entry: MenuEntry): string | undefined => {
  if (!entry.count || entry.countNoun == null) return undefined;
  const noun = entry.count === 1 ? entry.countNoun : `${entry.countNoun}s`;
  return `${entry.label}, ${entry.count} unread ${noun}`;
};

export type ProfileDropdownProps = {
  userName: string;
  userAffiliation?: string | null;
  avatarUrl: string;
  profileUrl: string;
  impactStats?: ImpactStats | null;
  messagesCount: number;
  notificationCount: number | null;
  isPremium: boolean;
  premiumUpsells?: PremiumUpsells;
  onClose?: () => void;
  hasUnseenMentions?: boolean;
  hasUnseenReaders?: boolean;
  links: NavImpactLinks;
};

const ProfileDropdown = ({
  userName,
  userAffiliation,
  avatarUrl,
  profileUrl,
  impactStats,
  messagesCount,
  notificationCount,
  isPremium,
  premiumUpsells,
  onClose,
  hasUnseenMentions,
  hasUnseenReaders,
  links,
}: ProfileDropdownProps) => {
  const firstCell = citationsCell(impactStats);
  const firstCount = firstCell.count;
  const paperViewsTotal = impactStats?.paperViewsTotal ?? null;

  const segment = segmentFor(links.premium, premiumUpsells, TRIGGER);

  const upgradeCtaUrl = segment?.url ?? links.dropdown.upgrade;

  const upgradeCtaText = segment?.copy ?? 'Upgrade to Premium';

  const coScientistHref = links.dropdown.coScientist;
  const coScientistEntries: MenuEntry[] =
    coScientistHref == null
      ? []
      : [
          {
            label: 'Co-scientist',
            icon: <Icon name="home_repair_service" />,
            href: coScientistHref,
          },
        ];

  const sections: MenuEntry[][] = [
    [
      {
        label: 'Messages',
        icon: <Icon name="mail" />,
        href: links.dropdown.messages,
        count: messagesCount,
        countNoun: 'message',
      },
      {
        label: 'Notifications',
        icon: <Icon name="visibility" />,
        href: links.dropdown.notifications,
        count: notificationCount,
        countNoun: 'notification',
      },
    ],
    [
      {
        label: 'Bookmarks',
        icon: <Icon name="bookmark" />,
        href: links.dropdown.bookmarks,
      },
      {
        label: 'Reading history',
        icon: <Icon name="schedule" />,
        href: links.dropdown.readingHistory,
      },
    ],
    [
      {
        label: 'Profile',
        icon: <ProfileHexagonIcon size={20} />,
        href: profileUrl,
      },
      ...coScientistEntries,
      {
        label: 'Publishing Hub',
        icon: <Icon name="account_balance" />,
        href: links.dropdown.publishingHub,
      },
      {
        label: 'Personal website',
        icon: <Icon name="laptop_windows" />,
        href: links.dropdown.personalWebsite,
      },
      {
        label: 'Account settings',
        icon: <Icon name="tune" />,
        href: links.dropdown.settings,
      },
    ],
    [
      {
        label: 'Log out',
        icon: <Icon name="start" />,
        href: links.dropdown.logout,
      },
    ],
  ];

  return (
    <Panel data-testid="profile-dropdown">
      {onClose != null && (
        <MobileCloseButton
          type="button"
          aria-label="Close menu"
          onClick={onClose}
          data-testid="profile-dropdown-close"
        >
          <Icon name="close" size={24} aria-hidden />
        </MobileCloseButton>
      )}
      <TopBlock>
        <HeaderLink href={profileUrl} data-testid="profile-dropdown-header">
          <AvatarImg src={avatarUrl} alt="" />
          <HeaderText>
            <UserName>{userName}</UserName>
            {userAffiliation != null && userAffiliation !== '' && (
              <Affiliation>{userAffiliation}</Affiliation>
            )}
          </HeaderText>
        </HeaderLink>
        {(firstCount != null || paperViewsTotal != null) && (
          <StatRow data-testid="profile-dropdown-stats">
            {firstCount != null && (
              <StatCard
                href={links.citations}
                data-testid="profile-stat-citations"
              >
                <StatContent>
                  {dotVisible(hasUnseenMentions, firstCount) && (
                    <StatDot data-testid="profile-stat-dot-citations" />
                  )}
                  <StatLabel>{firstCell.label}</StatLabel>
                  <StatNumber>{formatCount(firstCount)}</StatNumber>
                </StatContent>
              </StatCard>
            )}
            {paperViewsTotal != null && (
              <StatCard
                href={links.paperViews}
                data-testid="profile-stat-paper-views"
              >
                <StatContent>
                  {dotVisible(hasUnseenReaders, paperViewsTotal) && (
                    <StatDot data-testid="profile-stat-dot-paper-views" />
                  )}
                  <StatLabel>Paper views</StatLabel>
                  <StatNumber>{formatCount(paperViewsTotal)}</StatNumber>
                </StatContent>
              </StatCard>
            )}
          </StatRow>
        )}
        {!isPremium && (
          <ImpressionWatcher source={TRIGGER}>
            <CtaButtonLink
              fullWidth
              href={upgradeCtaUrl}
              icon={
                <PremiumLock circleColor="white" lockColor="blue" width={20} />
              }
              data-testid="profile-dropdown-cta"
            >
              {upgradeCtaText}
            </CtaButtonLink>
          </ImpressionWatcher>
        )}
      </TopBlock>
      <nav aria-label="Account">
        {sections.map((entries) => (
          <Section
            key={entries[0].label}
            data-testid="profile-dropdown-section"
          >
            {entries.map((entry) => (
              <li key={entry.label}>
                <RowLink
                  href={entry.href}
                  aria-label={unreadAriaLabel(entry)}
                  data-testid="profile-dropdown-item"
                >
                  <RowIcon>{entry.icon}</RowIcon>
                  <span data-testid="profile-dropdown-item-label">
                    {entry.label}
                  </span>
                  {entry.count != null && <CountPill count={entry.count} />}
                </RowLink>
              </li>
            ))}
          </Section>
        ))}
      </nav>
    </Panel>
  );
};

export default ProfileDropdown;
